import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const NAV_ITEMS = [
  { label: "首页", to: "/" },
  { label: "影视分类", to: "/category" },
  { label: "影视资讯", to: "/news" },
];

export default function SiteHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  const solid = scrolled || !isHome;

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    const value = keyword.trim();
    navigate(value ? `/search?q=${encodeURIComponent(value)}` : "/search");
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        solid
          ? "bg-background-50/95 backdrop-blur-md border-b border-background-200"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto flex h-16 max-w-shell items-center gap-4 px-4 md:h-[72px] md:gap-8 md:px-6">
        <Link to="/" className="flex shrink-0 items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-500 text-background-50">
            <i className="ri-movie-2-fill text-[22px]" />
          </span>
          <span className="flex flex-col leading-none">
            <span
              className={`font-heading text-[17px] font-bold tracking-tight md:text-[19px] ${
                solid ? "text-foreground-950" : "text-background-50"
              }`}
            >
              色戒
              <span className="text-accent-500"> 完整版</span>
            </span>
            <span
              className={`mt-1 font-label text-[10px] uppercase tracking-[0.22em] ${
                solid ? "text-foreground-500" : "text-background-300"
              }`}
            >
              Uncut · HD Portal
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 md:flex">
          {NAV_ITEMS.map((item) => {
            const active =
              item.to === "/" ? location.pathname === "/" : location.pathname.startsWith(item.to);
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`whitespace-nowrap rounded-md px-3 py-2 text-sm transition-colors ${
                  active
                    ? "font-medium text-accent-500"
                    : solid
                      ? "text-foreground-700 hover:text-foreground-950"
                      : "text-background-100 hover:text-background-50"
                }`}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="ml-auto hidden items-center gap-3 md:flex">
          <form onSubmit={submitSearch} className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400" />
            <input
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索影片、演员或资讯"
              aria-label="站内搜索"
              className={`h-10 w-[220px] rounded-md border pl-9 pr-3 text-sm outline-none transition-colors lg:w-[260px] ${
                solid
                  ? "border-background-300 bg-background-100 text-foreground-900 placeholder:text-foreground-400 focus:border-accent-400"
                  : "border-background-50/30 bg-background-50/10 text-background-50 placeholder:text-background-200 focus:border-accent-400"
              }`}
            />
          </form>
          <Link
            to="/search"
            className="whitespace-nowrap rounded-md bg-accent-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600"
          >
            找片
          </Link>
        </div>

        <button
          type="button"
          aria-label="打开菜单"
          onClick={() => setMenuOpen((prev) => !prev)}
          className={`ml-auto flex h-10 w-10 items-center justify-center rounded-md border md:hidden ${
            solid
              ? "border-background-300 text-foreground-800"
              : "border-background-50/40 text-background-50"
          }`}
        >
          <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-3-line text-xl"} />
        </button>
      </div>

      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 pb-5 pt-4 md:hidden">
          <form onSubmit={submitSearch} className="relative mb-3">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400" />
            <input
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索影片、演员或资讯"
              aria-label="移动端站内搜索"
              className="h-11 w-full rounded-md border border-background-300 bg-background-100 pl-9 pr-3 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-accent-400"
            />
          </form>
          <div className="flex flex-col">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="flex items-center justify-between border-b border-background-200 py-3 text-sm text-foreground-800"
              >
                {item.label}
                <i className="ri-arrow-right-s-line text-foreground-400" />
              </Link>
            ))}
            <Link
              to="/search"
              className="mt-4 flex items-center justify-center gap-2 rounded-md bg-primary-500 py-3 text-sm font-medium text-background-50"
            >
              <i className="ri-search-2-line" />
              进入搜索页
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}